/**
 * Theme CSS + bridge scripts injected into every widget iframe (Phase J,
 * ported/adapted from cycle-3.0). THEME_CSS maps the widget design contract
 * (interactive.md) onto cycledesign B2B Slate tokens and pre-styles bare form
 * elements; BRIDGE_SCRIPTS provides sendPrompt/openLink/resize + the streaming
 * __wca_html/__wca_commit protocol. The iframe is isolated, so it cannot
 * inherit host CSS vars — they are defined here (light + prefers-color-scheme dark).
 */

/**
 * Strict CSP for the widget iframe shell (OpenSpec §16.4 / codex §4 HIGH).
 * Prepended to the srcDoc so it governs the document before any commit-time
 * activity. Rationale per directive:
 * - default-src 'none' / base-uri 'none': deny everything not explicitly allowed.
 * - script-src 'unsafe-inline' 'unsafe-eval': the inline bridge + the eval'd
 *   widget JS run, but NO external script host is permitted (kills attacker-CDN
 *   loading).
 * - style-src 'unsafe-inline' + the Google-Fonts host: the shell's own theme
 *   and its `@import` font stylesheet (host-controlled, not widget-controlled).
 * - font-src: the Google-Fonts font files + inline data.
 * - img-src data: blob:: inline images only — no external image beacon exfil.
 * - connect-src 'none': blocks all fetch/XHR/sendBeacon/WebSocket egress, so a
 *   widget cannot phone the conversation home.
 * - form-action 'none': blocks native form POST to an external target (widgets
 *   submit via the sendPrompt postMessage bridge, not native navigation).
 */
export const WIDGET_CSP_META =
  `<meta http-equiv="Content-Security-Policy" content="` +
  `default-src 'none'; ` +
  `script-src 'unsafe-inline' 'unsafe-eval'; ` +
  `style-src 'unsafe-inline' https://fonts.googleapis.com; ` +
  `font-src https://fonts.gstatic.com data:; ` +
  `img-src data: blob:; ` +
  `connect-src 'none'; ` +
  `form-action 'none'; ` +
  `base-uri 'none'">`;

export const THEME_CSS = `<style>
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');
:root {
  --color-text-primary: #0F1320;
  --color-text-secondary: hsl(220, 11%, 32%);
  --color-text-tertiary: hsl(220, 9%, 55%);
  --color-text-info: hsl(200, 85%, 30%);
  --color-text-success: hsl(142, 65%, 26%);
  --color-text-warning: hsl(32, 90%, 34%);
  --color-text-danger: hsl(354, 70%, 40%);
  --color-background-primary: #FFFFFF;
  --color-background-secondary: hsl(220, 20%, 98%);
  --color-background-tertiary: hsl(220, 14%, 95%);
  --color-background-info: hsl(200, 90%, 94%);
  --color-background-success: hsl(142, 70%, 92%);
  --color-background-warning: hsl(38, 92%, 92%);
  --color-background-danger: hsl(354, 80%, 94%);
  --color-border-primary: hsl(220, 11%, 70%);
  --color-border-secondary: hsl(220, 11%, 82%);
  --color-border-tertiary: hsl(220, 13%, 90%);
  --color-brand: hsl(215, 25%, 27%);
  /* Fixed button-fill colors (do NOT flip per theme): models hardcode
   * white button text on filled buttons, so the fill must stay dark enough for
   * white text in BOTH light and dark mode, while staying lighter than the
   * dark card so it's distinguishable. --color-brand itself flips light in
   * dark mode (it is an accent/text color), so it cannot be a fill. */
  --brand-fill: hsl(215, 28%, 32%);
  --danger-fill: hsl(354, 58%, 44%);
  --success-fill: hsl(142, 50%, 32%);
  --warning-fill: hsl(32, 70%, 40%);
  /* Aliases for the common var names models tend to invent, mapped onto the
   * real B2B Slate tokens (or the fixed fills) so an off-spec widget still
   * renders correctly. Button-fill aliases point at the fixed fills; surface
   * and text aliases point at the theme-flipping tokens. */
  --primary-color: var(--brand-fill);
  --accent-color: var(--brand-fill);
  --secondary-color: var(--color-text-secondary);
  --danger-color: var(--danger-fill);
  --success-color: var(--success-fill);
  --warning-color: var(--warning-fill);
  --border-color: var(--color-border-secondary);
  --background-color: var(--color-background-primary);
  --surface-color: var(--color-background-secondary);
  --text-color: var(--color-text-primary);
  --text-secondary: var(--color-text-secondary);
  --text-muted: var(--color-text-tertiary);
  --font-sans: "IBM Plex Sans", system-ui, -apple-system, "PingFang SC", "Microsoft YaHei", sans-serif;
  --font-serif: Georgia, "Songti SC", serif;
  --font-mono: "IBM Plex Mono", "SF Mono", "Fira Code", monospace;
  --border-radius-sm: 4px;
  --border-radius-md: 6px;
  --border-radius-lg: 8px;
  --border-radius-xl: 12px;
  /* Host-shell token names (mirror web/src/styles/tokens.css) so widget CSS can
     use the SAME token names as the app shell and stay visually aligned rather
     than drifting on a parallel set. The --color-*/--border-radius-* names above
     remain as back-compat aliases. var()-based aliases below cascade into dark. */
  --text-primary: var(--color-text-primary);
  --text-tertiary: var(--color-text-tertiary);
  --text-brand: var(--color-brand);
  --text-inverse: #ffffff;
  --bg-base: var(--color-background-secondary);
  --bg-surface: var(--color-background-primary);
  --bg-raised: var(--color-background-tertiary);
  --border-color: var(--color-border-tertiary);
  --border-color-strong: var(--color-border-secondary);
  --border-strong: var(--color-border-secondary);
  --radius-xs: 2px;
  --radius-sm: 4px;
  --radius-md: 6px;
  --radius-lg: 8px;
  --radius-xl: 12px;
  --radius-2xl: 16px;
  --radius-full: 9999px;
  --color-primary-50: hsl(214, 32%, 96%);
  --color-primary-100: hsl(214, 28%, 91%);
  --color-primary-200: hsl(214, 24%, 83%);
  --color-primary-500: hsl(215, 22%, 35%);
  --color-primary-600: hsl(215, 25%, 27%);
  --color-primary-700: hsl(217, 28%, 21%);
  --font-display: "Fraunces", "Times New Roman", serif;
  --font-body: var(--font-sans);
  --shadow-xs: 0 1px 2px rgba(15, 23, 42, 0.05);
  --shadow-sm: 0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.05);
  --shadow-md: 0 4px 8px rgba(15, 23, 42, 0.07), 0 2px 4px rgba(15, 23, 42, 0.05);
  --shadow-lg: 0 10px 16px rgba(15, 23, 42, 0.08), 0 4px 6px rgba(15, 23, 42, 0.04);
}
/* The host app is fixed to dark mode (<html data-mode="dark">), and the
 * sandboxed iframe cannot see the host's data-mode — so the widget theme must
 * be dark unconditionally rather than keying off the OS prefers-color-scheme
 * (which mismatched: white inputs in a dark app). These dark values come after
 * the light :root above. The host passes its resolved mode into the iframe
 * (WidgetRenderer → __wca_mode → data-cc-mode on the root), so widgets follow
 * the host's light/dark toggle. Default (before the message lands) = dark,
 * since the host defaults to dark. */
:root[data-cc-mode="dark"], :root:not([data-cc-mode]) {
  --color-text-primary: #F4F5F9;
  --color-text-secondary: hsl(228, 10%, 70%);
  --color-text-tertiary: hsl(228, 8%, 50%);
  --color-text-info: hsl(200, 80%, 70%);
  --color-text-success: hsl(142, 60%, 62%);
  --color-text-warning: hsl(38, 90%, 66%);
  --color-text-danger: hsl(354, 85%, 72%);
  --color-background-primary: hsl(228, 12%, 14%);
  --color-background-secondary: hsl(228, 10%, 17%);
  --color-background-tertiary: hsl(228, 10%, 22%);
  --color-background-info: hsl(200, 50%, 20%);
  --color-background-success: hsl(142, 45%, 18%);
  --color-background-warning: hsl(38, 55%, 18%);
  --color-background-danger: hsl(354, 50%, 22%);
  --color-border-primary: hsl(228, 10%, 34%);
  --color-border-secondary: hsl(228, 10%, 26%);
  --color-border-tertiary: hsl(228, 10%, 20%);
  --color-brand: hsl(213, 40%, 72%);
  /* Dark-mode overrides for the mode-variant host tokens (text-inverse +
     stronger elevation, matching tokens.css dark). The var()-based aliases
     above (text-primary, bg-*, border-*) cascade automatically. */
  --text-inverse: hsl(228, 14%, 9%);
  --shadow-xs: 0 1px 2px rgba(0, 0, 0, 0.5);
  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.6), 0 1px 2px rgba(0, 0, 0, 0.4);
  --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.6), 0 2px 4px rgba(0, 0, 0, 0.4);
  --shadow-lg: 0 12px 24px rgba(0, 0, 0, 0.65), 0 4px 8px rgba(0, 0, 0, 0.4);
}
* { box-sizing: border-box; }
body {
  margin: 0;
  padding: 4px 20px 20px;
  font-family: var(--font-sans);
  font-size: 14px;
  line-height: 1.65;
  color: var(--color-text-primary);
  background: transparent;
}
h1 { font-size: 18px; font-weight: 600; margin: 0 0 10px; }
h2 { font-size: 16px; font-weight: 600; margin: 0 0 8px; }
h3 { font-size: 14px; font-weight: 600; margin: 0 0 6px; }
p { margin: 0 0 10px; }
label { font-size: 13px; color: var(--color-text-secondary); }
input[type="text"], input[type="number"], input[type="email"], select, textarea {
  font-family: var(--font-sans);
  font-size: 14px;
  color: var(--color-text-primary);
  background: var(--color-background-primary);
  border: 1.5px solid var(--color-border-primary);
  border-radius: var(--border-radius-md);
  padding: 8px 12px;
  min-height: 38px;
  width: 100%;
  outline: none;
  transition: border-color 120ms ease-out, box-shadow 120ms ease-out;
}
input:focus, select:focus, textarea:focus {
  border-color: var(--color-brand);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-brand) 22%, transparent);
}
textarea { min-height: 72px; resize: vertical; }
button {
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
  background: transparent;
  border: 1px solid var(--color-border-primary);
  border-radius: var(--border-radius-md);
  padding: 8px 14px;
  cursor: pointer;
  transition: background 120ms ease-out, transform 80ms ease-out;
}
button:hover { background: var(--color-background-secondary); }
button:active { transform: scale(0.98); }
button.primary {
  background: var(--brand-fill);
  border-color: var(--brand-fill);
  color: #FFFFFF;
}
input[type="range"] { width: 100%; accent-color: var(--color-brand); }
</style>`;

export const BRIDGE_SCRIPTS = `<script>
(function() {
  "use strict";
  function sendPrompt(text) {
    if (typeof text !== "string") return;
    window.parent.postMessage({ type: "sendPrompt", text: text }, "*");
  }
  function openLink(url) {
    if (typeof url !== "string") return;
    window.parent.postMessage({ type: "openLink", url: url }, "*");
  }
  function recordFeedback(reason, note) {
    if (typeof reason !== "string") return;
    window.parent.postMessage(
      { type: "feedback", reason: reason, note: typeof note === "string" ? note : undefined },
      "*"
    );
  }
  window.sendPrompt = sendPrompt;
  window.openLink = openLink;
  window.recordFeedback = recordFeedback;

  var wrapper = document.createElement("div");
  wrapper.id = "__wca_root";
  var lastH = 0;
  function postHeight() {
    // Use the full document height (includes body padding + last-child margins)
    // and a small buffer so content is never clipped. Re-measured on layout
    // changes, after script commit, on font load, and on a few timers — fonts
    // load async and reflow taller, which was clipping buttons.
    var de = document.documentElement;
    var h = Math.max(de.scrollHeight, document.body.scrollHeight, wrapper.scrollHeight) + 6;
    if (h !== lastH) { lastH = h; window.parent.postMessage({ type: "resize", height: h }, "*"); }
  }
  function initWrapper() {
    while (document.body.firstChild) wrapper.appendChild(document.body.firstChild);
    document.body.appendChild(wrapper);
    var ro = new ResizeObserver(postHeight);
    ro.observe(wrapper);
    ro.observe(document.documentElement);
    window.addEventListener("load", postHeight);
    if (document.fonts && document.fonts.ready) { document.fonts.ready.then(postHeight); }
    [80, 250, 600].forEach(function(t) { setTimeout(postHeight, t); });
  }
  if (document.body) { initWrapper(); } else { document.addEventListener("DOMContentLoaded", initWrapper); }

  window.onerror = function(msg, src, line, col) {
    window.parent.postMessage({ type: "widgetError", error: String(msg), line: line, col: col }, "*");
  };

  window.addEventListener("message", function(e) {
    var d = e.data;
    if (!d || typeof d.type !== "string") return;
    if (d.type === "__wca_mode" && (d.mode === "light" || d.mode === "dark")) {
      document.documentElement.setAttribute("data-cc-mode", d.mode);
      setTimeout(postHeight, 0);
      return;
    }
    if (d.type === "__wca_html" && typeof d.value === "string") {
      var root = document.getElementById("__wca_root") || document.body;
      root.innerHTML = d.value;
      setTimeout(postHeight, 0);
      return;
    }
    if (d.type === "__wca_commit") {
      // Hardened commit (§16.4): self-contained widgets carry inline scripts
      // only. External <script src> is not part of the contract, is blocked by
      // CSP, and is dropped here (defense in depth) rather than re-activated.
      var root = document.getElementById("__wca_root") || document.body;
      var scripts = Array.from(root.querySelectorAll("script"));
      scripts.forEach(function(old) {
        if (old.src) {
          console.warn("[widget] external script blocked:", old.src);
          old.parentNode.removeChild(old);
          return;
        }
        var code = old.textContent;
        old.parentNode.removeChild(old);
        try { (0, eval)(code); } catch (err) { console.error("[widget] script error:", err); }
      });
      return;
    }
  });
})();
</script>`;
